import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
} from '@mui/material';

interface NO4ReportTableProps {
  data: any;
  direction?: string;
  tableIndex?: number;
}

// Column headers for the NO4 report
const columnHeaders = [
  'P Petit',
  'G Grand',
  'E Encombrant\n(sampled flow)',
  'E Encombrant\n(non-sampled flow)',
  'PGE Mixed\n(sampled flow)',
  'PGE Mixed\n(non-sampled flow)',
  'C Postcards',
  'M Bags\nSubject to TD',
  'Registered R',
  'Insured V',
  'Express',
];

// Section 1 rows
const section1Rows = [
  { label: 'Yearly total weight CN56', unit: 'kg', key: 'yearlyTotalWeightCn56' },
  { label: 'Yearly total items CN56', unit: 'items', key: 'yearlyTotalItemsCn56' },
  {
    label: 'Adjustment factor gross to net from NO3',
    unit: '%',
    key: 'adjustmentFactorGrossToNetFromNo3',
  },
  { label: 'Net weight', unit: 'kg', key: 'netWeight' },
  { label: 'Net non-exempt IBRS to be deducted', unit: 'kg', key: 'netNonExemptIbrsToBeDeducted' },
  { label: 'Net IB weight to be deducted', unit: 'kg', key: 'netIbWeightToBeDeducted' },
  {
    label: 'Total net weight subject to TD',
    unit: 'kg',
    isTotal: true,
    key: 'totalNetWeightSubjectToTd',
  },
  {
    label: 'Total number of items CN56',
    unit: 'items',
    isTotal: true,
    key: 'totalNumberOfItemsCn56',
  },
];

// Section 2 rows
const section2Rows = [
  { label: 'Format separated despatches', isSubtitle: true, key: 'formatSeparatedDespatches' },
  {
    label: 'Net weight (from format separated despatches in section 1)',
    unit: 'kg',
    key: 'netWeightFromFormatSeparatedDespatches',
  },
  { label: 'IPK (from format separated NO3)', unit: '', key: 'ipkFromFormatSeparatedNo3' },
  {
    label: 'Total number of items',
    unit: 'items',
    isTotal: true,
    key: 'totalNumberOfItemsFormatSeparatedDespatches',
  },
  { label: 'Mixed despatches', isSubtitle: true, key: 'mixedDespatches' },
  {
    label: 'Net weight (from mixed despatches in section 1)',
    unit: 'kg',
    key: 'netWeightFromMixedDespatches',
  },
  {
    label: 'Net weight proportion by format (from mixed NO3)',
    unit: '%',
    key: 'netWeightProportionByFormat',
  },
  { label: 'Net weight by format', unit: 'kg', key: 'netWeightByFormat' },
  { label: 'IPK (from mixed NO3)', unit: '', key: 'ipkFromMixedNo3' },
  {
    label: 'Total number of items',
    unit: 'items',
    isTotal: true,
    key: 'totalNumberOfItemsMixedDespatches',
  },
];

// Section 3 rows
const section3Rows = [
  {
    label: 'Total net weight to be accounted',
    unit: 'kg',
    isTotal: true,
    key: 'totalNetWeightToBeAccounted',
  },
  {
    label: 'Total number of items to be accounted',
    unit: 'items',
    isTotal: true,
    key: 'totalNumberOfItemsToBeAccounted',
  },
];

const formatNumber = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '';
  return value.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatPercentage = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '';
  return `${value.toFixed(2)}%`;
};

const NO4ReportTable: React.FC<NO4ReportTableProps> = ({ data, direction, tableIndex = 0 }) => {
  const {
    no3Cn56Summary,
    cn56ItemsAndNetWeightSummary,
    calculationNumberOfItemsByFormat,
    cn61Totals,
    hasAlerts,
  } = data;

  // Determine if this table represents inbound or outbound data
  const getTableDirection = () => {
    if (direction === 'Inbound' || direction === 'INBOUND') {
      return 'inbound';
    }
    if (direction === 'Outbound' || direction === 'OUTBOUND') {
      return 'outbound';
    }

    // Note: The direction determination logic is now handled in the parent component
    // based on the current user's designated operator vs receiving PPO
    // So we should no longer get "All" here, but keeping fallback logic
    if (direction === 'All' || direction === 'ALL') {
      // Fallback: alternate between inbound and outbound based on table index
      return tableIndex % 2 === 0 ? 'inbound' : 'outbound';
    }

    return 'inbound'; // Default
  };

  // Get background color for table cells based on direction
  const getCellBackgroundColor = (originalColor: string, isGrayCell: boolean = false) => {
    const tableDirection = getTableDirection();

    // Gray cells keep their gray color
    if (isGrayCell) {
      return '#AFAFAF';
    }

    // Apply direction colors to all regular cells (including totals)
    if (
      originalColor === 'inherit' ||
      originalColor === '#fafafa' ||
      originalColor === '#e8f5e8' ||
      originalColor === '#f0f8f0'
    ) {
      return tableDirection === 'inbound' ? '#f0f9f0' : '#fff0f0';
    }

    // Keep only gray cells as special
    return originalColor;
  };

  // Helper function to get the appropriate column keys based on row type and section
  const getColumnKeysForRow = (section: string, rowKey: string): string[] => {
    // Define column key mappings for different row types
    // The order must match the columnHeaders array: ['P Petit', 'G Grand', 'E Encombrant\n(sampled flow)', 'E Encombrant\n(non-sampled flow)', 'PGE Mixed\n(sampled flow)', 'PGE Mixed\n(non-sampled flow)', 'C Postcards', 'M Bags\nSubject to TD', 'Registered R', 'Insured V', 'Express']
    const columnKeyMappings: { [key: string]: string[] } = {
      // Section 1 mappings
      yearlyTotalWeightCn56: [
        'ppetit',
        'ggrand',
        'eencombrantWithoutUr',
        'eencombrantUrOnly',
        'pgeMixedWithoutUr',
        'pgeMixedUrOnly',
        'cpostCards',
        'mbagsSubjectToTd',
        '',
        '',
        '',
      ],
      yearlyTotalItemsCn56: [
        '',
        '',
        '',
        'eencombrantUrOnly',
        '',
        'pgeMixedUrOnly',
        '',
        '',
        'registeredR',
        'insuredV',
        'express',
      ],
      adjustmentFactorGrossToNetFromNo3: [
        'ppetit',
        'ggrand',
        'eencombrantWithoutUr',
        'eencombrantUrOnly',
        'pgeMixedWithoutUr',
        'pgeMixedUrOnly',
        'cpostCards',
        '',
        '',
        '',
        '',
      ],
      netWeight: [
        'ppetit',
        'ggrand',
        'eencombrantWithoutUr',
        'eencombrantUrOnly',
        'pgeMixedWithoutUr',
        'pgeMixedUrOnly',
        'cpostCards',
        '',
        '',
        '',
        '',
      ],
      netNonExemptIbrsToBeDeducted: [
        '',
        '',
        'eencombrantWithoutUr',
        'eencombrantUrOnly',
        'pgeMixedWithoutUr',
        '',
        '',
        '',
        '',
        '',
        '',
      ],
      netIbWeightToBeDeducted: ['', '', 'eencombrantWithoutUr', '', '', '', '', '', '', '', ''],
      totalNetWeightSubjectToTd: [
        'ppetit',
        'ggrand',
        'eencombrantWithoutUr',
        'eencombrantUrOnly',
        'pgeMixedWithoutUr',
        'pgeMixedUrOnly',
        'cpostCards',
        'mbagsSubjectToTd',
        '',
        '',
        '',
      ],
      totalNumberOfItemsCn56: [
        '',
        '',
        '',
        'eencombrantUrOnly',
        '',
        'pgeMixedUrOnly',
        '',
        '',
        'registeredR',
        'insuredV',
        'express',
      ],

      // Section 2 mappings
      netWeightFromFormatSeparatedDespatches: [
        'ppetit',
        'ggrand',
        'eencombrantWithoutUr',
        'eencombrantUrOnly',
        '',
        '',
        'cpostCards',
        '',
        '',
        '',
        '',
      ],
      ipkFromFormatSeparatedNo3: [
        'ppetit',
        'ggrand',
        'eencombrantWithoutUr',
        '',
        '',
        '',
        'cpostCards',
        '',
        '',
        '',
        '',
      ],
      totalNumberOfItemsFormatSeparatedDespatches: [
        'ppetit',
        'ggrand',
        'eencombrantWithoutUr',
        'eencombrantUrOnly',
        '',
        '',
        'cpostCards',
        '',
        '',
        '',
        '',
      ],
      netWeightFromMixedDespatches: [
        '',
        '',
        '',
        'eencombrantUrOnly',
        'pgeMixedWithoutUr',
        'pgeMixedUrOnly',
        '',
        '',
        '',
        '',
        '',
      ],
      netWeightProportionByFormat: [
        'ppetit',
        'ggrand',
        'eencombrantWithoutUr',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
      ],
      netWeightByFormat: [
        'ppetit',
        'ggrand',
        'eencombrantWithoutUr',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
      ],
      ipkFromMixedNo3: ['ppetit', 'ggrand', 'eencombrantWithoutUr', '', '', '', '', '', '', '', ''],
      totalNumberOfItemsMixedDespatches: [
        'ppetit',
        'ggrand',
        'eencombrantWithoutUr',
        'eencombrantUrOnly',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
      ],

      // Section 3 mappings
      totalNetWeightToBeAccounted: [
        'ppetit',
        'ggrand',
        'eencombrantWithoutUr',
        '',
        '',
        '',
        'cpostCards',
        'mbagsSubjectToTd',
        '',
        '',
        '',
      ],
      totalNumberOfItemsToBeAccounted: [
        'ppetit',
        'ggrand',
        'eencombrantWithoutUr',
        '',
        '',
        '',
        'cpostCards',
        '',
        'registeredR',
        'insuredV',
        'express',
      ],
    };

    return columnKeyMappings[rowKey] || [];
  };

  // Helper function to get value from the actual data structure
  const getValueFromData = (section: string, rowKey: string, columnKey: string): any => {
    try {
      let value = null;

      switch (section) {
        case 'section1':
          const section1Data = cn56ItemsAndNetWeightSummary?.[rowKey];
          if (section1Data && columnKey) {
            // Try to get formatted value first, then fall back to raw value
            value = section1Data[`${columnKey}Formatted`] || section1Data[columnKey];
          }
          break;
        case 'section2':
          // Section 2 has nested structure: formatSeparatedDespatches and mixedDespatches
          // Check both subsections for the rowKey
          const formatSeparatedData =
            calculationNumberOfItemsByFormat?.formatSeparatedDespatches?.[rowKey];
          const mixedData = calculationNumberOfItemsByFormat?.mixedDespatches?.[rowKey];

          if (formatSeparatedData && columnKey) {
            value = formatSeparatedData[`${columnKey}Formatted`] || formatSeparatedData[columnKey];
          } else if (mixedData && columnKey) {
            value = mixedData[`${columnKey}Formatted`] || mixedData[columnKey];
          }
          break;
        case 'section3':
          const section3Data = cn61Totals?.[rowKey];
          if (section3Data && columnKey) {
            // Try to get formatted value first, then fall back to raw value
            value = section3Data[`${columnKey}Formatted`] || section3Data[columnKey];
          }
          break;
        default:
          value = null;
      }

      return value;
    } catch (error) {
      console.error('Error getting value:', error);
      return null;
    }
  };

  // Helper function to check if a field source is bilateral
  const isBilateralSource = (section: string, rowKey: string, columnKey: string): boolean => {
    try {
      let sourceValue = null;

      switch (section) {
        case 'section1':
          const section1Data = cn56ItemsAndNetWeightSummary?.[rowKey];
          if (section1Data && columnKey) {
            sourceValue = section1Data[`${columnKey}Source`];
          }
          break;
        case 'section2':
          // Section 2 has nested structure: formatSeparatedDespatches and mixedDespatches
          const formatSeparatedData =
            calculationNumberOfItemsByFormat?.formatSeparatedDespatches?.[rowKey];
          const mixedData = calculationNumberOfItemsByFormat?.mixedDespatches?.[rowKey];

          if (formatSeparatedData && columnKey) {
            sourceValue = formatSeparatedData[`${columnKey}Source`];
          } else if (mixedData && columnKey) {
            sourceValue = mixedData[`${columnKey}Source`];
          }
          break;
        case 'section3':
          const section3Data = cn61Totals?.[rowKey];
          if (section3Data && columnKey) {
            sourceValue = section3Data[`${columnKey}Source`];
          }
          break;
        default:
          sourceValue = null;
      }

      return sourceValue === 'bilateral';
    } catch (error) {
      console.error('Error checking bilateral source:', error);
      return false;
    }
  };

  // Helper function to get formatted value from actual data structure
  const getFormattedValueFromData = (
    section: string,
    rowKey: string,
    columnKey: string,
    isPercentage: boolean = false
  ): string => {
    const value = getValueFromData(section, rowKey, columnKey);

    // If value is null or undefined, return empty string
    if (value === null || value === undefined) return '';

    // If the value is already a string (formatted), return it as is
    if (typeof value === 'string') {
      return value;
    }

    // If it's a number, format it according to the type
    if (typeof value === 'number') {
      if (isPercentage) {
        return formatPercentage(value);
      }
      return formatNumber(value);
    }

    // Fallback to string representation
    return String(value);
  };

  return (
    <Box sx={{ mt: 6 }}>
      {/* Report Header - Similar to NO2Report.tsx */}
      <Box
        sx={{
          mb: 3,
          p: 3,
          border: '1px solid #E1E8ED',
          borderRadius: '8px',
          backgroundColor: '#FAFBFC',
        }}
      >
        {/* Report Title */}
        <Box
          sx={{
            mb: 3,
            pb: 2,
            borderBottom: '1px solid #E1E8ED',
          }}
        >
          <Typography
            variant='h5'
            sx={{
              fontWeight: 600,
              color: '#063142',
              fontSize: '20px',
              letterSpacing: '-0.25px',
            }}
          >
            NO4 Report - Terminal Dues Accounting
          </Typography>
        </Box>

        {/* Report Details */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 3,
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 200 }}>
            <Typography
              variant='body2'
              sx={{
                fontWeight: 600,
                color: '#063142',
                fontSize: '14px',
              }}
            >
              Sending DO
            </Typography>
            <Typography
              variant='body1'
              sx={{
                color: '#666',
                fontSize: '14px',
                fontFamily: 'monospace',
                backgroundColor: '#FFFFFF',
                padding: '4px 8px',
                borderRadius: '3px',
                display: 'inline-block',
              }}
            >
              {no3Cn56Summary?.sendingPpo || '-'}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 200 }}>
            <Typography
              variant='body2'
              sx={{
                fontWeight: 600,
                color: '#063142',
                fontSize: '14px',
              }}
            >
              Receiving DO
            </Typography>
            <Typography
              variant='body1'
              sx={{
                color: '#666',
                fontSize: '14px',
                fontFamily: 'monospace',
                backgroundColor: '#FFFFFF',
                padding: '4px 8px',
                borderRadius: '3px',
                display: 'inline-block',
              }}
            >
              {no3Cn56Summary?.receivingPpo || '-'}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 120 }}>
            <Typography
              variant='body2'
              sx={{
                fontWeight: 600,
                color: '#063142',
                fontSize: '14px',
              }}
            >
              Year
            </Typography>
            <Typography
              variant='body1'
              sx={{
                color: '#666',
                fontSize: '14px',
                fontFamily: 'monospace',
                backgroundColor: '#FFFFFF',
                padding: '4px 8px',
                borderRadius: '3px',
                display: 'inline-block',
              }}
            >
              {no3Cn56Summary?.year || '-'}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 150 }}>
            <Typography
              variant='body2'
              sx={{
                fontWeight: 600,
                color: '#063142',
                fontSize: '14px',
              }}
            >
              Accounting Type
            </Typography>
            <Typography
              variant='body1'
              sx={{
                color: '#666',
                fontSize: '14px',
                fontFamily: 'monospace',
                backgroundColor: '#FFFFFF',
                padding: '4px 8px',
                borderRadius: '3px',
                display: 'inline-block',
              }}
            >
              {no3Cn56Summary?.accountingType || '-'}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 150 }}>
            <Typography
              variant='body2'
              sx={{
                fontWeight: 600,
                color: '#063142',
                fontSize: '14px',
              }}
            >
              Mail Category
            </Typography>
            <Typography
              variant='body1'
              sx={{
                color: '#666',
                fontSize: '14px',
                fontFamily: 'monospace',
                backgroundColor: '#FFFFFF',
                padding: '4px 8px',
                borderRadius: '3px',
                display: 'inline-block',
              }}
            >
              {no3Cn56Summary?.mailCategoryEnum || '-'}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Error message when hasAlerts is true */}
      {hasAlerts && (
        <Alert
          severity='error'
          sx={{
            mt: 2,
            '& .MuiAlert-message': {
              fontSize: '16px',
              fontWeight: 500,
            },
          }}
        >
          The NO3 & CN56 Summary is incomplete and thus the NO4 cannot be generated. Please complete
          the NO3 & CN56 Summary and try again.
        </Alert>
      )}
      {/* Show data table only if there are no alerts */}
      {!hasAlerts && (
        <TableContainer component={Paper} sx={{ boxShadow: 2, maxWidth: '1550px' }}>
          <Table sx={{ minWidth: 1000 }} aria-label='NO4 report table'>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#E7EFF5' }}>
                {/* Empty cell for row labels */}
                <TableCell
                  sx={{
                    minWidth: 240,
                    fontWeight: 600,
                    fontSize: '11px',
                    borderRight: '2px solid #ddd',
                    border: '1px solid #ddd',
                    color: '#063142',
                    py: 0.25,
                  }}
                >
                  Metric
                </TableCell>
                {/* Column headers */}
                {columnHeaders.map((header, index) => (
                  <TableCell
                    key={index}
                    align='center'
                    sx={{
                      minWidth: 90,
                      fontWeight: 600,
                      fontSize: '11px',
                      whiteSpace: 'pre-line',
                      textAlign: 'center',
                      verticalAlign: 'top',
                      py: 0.25,
                      px: 0.25,
                      border: '1px solid #ddd',
                      color: '#063142',
                    }}
                  >
                    {header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {/* Section 1: CN56 items and net weight summary */}
              <TableRow sx={{ backgroundColor: '#E7EFF5' }}>
                <TableCell
                  colSpan={12}
                  sx={{
                    fontWeight: 700,
                    fontSize: '14px',
                    padding: '8px',
                    backgroundColor: '#E7EFF5',
                    color: '#063142',
                    textAlign: 'left',
                    border: '1px solid #ddd',
                  }}
                >
                  1. CN56 items and net weight summary and adjustment from gross to net weight
                </TableCell>
              </TableRow>

              {section1Rows.map((row, index) => (
                <TableRow
                  key={`section1-${index}`}
                  sx={{
                    backgroundColor: getCellBackgroundColor('inherit'),
                    '&:nth-of-type(even)': { backgroundColor: getCellBackgroundColor('#fafafa') },
                  }}
                >
                  <TableCell
                    sx={{
                      fontWeight: row.isTotal ? 700 : 500,
                      borderRight: '2px solid #ddd',
                      backgroundColor: getCellBackgroundColor('inherit'),
                      border: '1px solid #ddd',
                      py: 0.5,
                    }}
                  >
                    {row.label} {row.unit && `(${row.unit})`}
                  </TableCell>
                  {columnHeaders.map((_, colIndex) => {
                    // Get the appropriate column keys for this row
                    const columnKeys = getColumnKeysForRow('section1', row.key);
                    const columnKey = columnKeys[colIndex];
                    const isBilateral = columnKey
                      ? isBilateralSource('section1', row.key, columnKey)
                      : false;

                    // Define columns that should have gray background for specific rows
                    const grayBackgroundColumnsByRow: { [key: string]: number[] } = {
                      yearlyTotalItemsCn56: [0, 1, 2, 4, 6, 7], // P Petit, G Grand, E Encombrant (sampled flow), PGE Mixed (sampled flow), C Postcards, M Bags Subject to TD
                      yearlyTotalWeightCn56: [8, 9, 10], // Registered R, Insured V, Express
                      adjustmentFactorGrossToNetFromNo3: [7, 8, 9, 10], // M Bags Subject to TD, Registered R, Insured V, Express
                      netWeight: [7, 8, 9, 10], // M Bags Subject to TD, Registered R, Insured V, Express
                      netNonExemptIbrsToBeDeducted: [0, 1, 5, 6, 7, 8, 9, 10], // P Petit, G Grand, PGE Mixed (non-sampled flow), C Postcards, M Bags Subject to TD, Registered R, Insured V, Express
                      netIbWeightToBeDeducted: [0, 1, 3, 4, 5, 6, 7, 8, 9, 10], // P Petit, G Grand, E Encombrant (non-sampled flow), PGE Mixed (sampled flow), PGE Mixed (non-sampled flow), C Postcards, M Bags Subject to TD, Registered R, Insured V, Express
                      totalNetWeightSubjectToTd: [8, 9, 10], // Registered R, Insured V, Express
                      totalNumberOfItemsCn56: [0, 1, 2, 4, 6, 7], // P Petit, G Grand, E Encombrant (sampled flow), PGE Mixed (sampled flow), C Postcards, M Bags Subject to TD
                    };
                    const shouldHaveGrayBackground =
                      grayBackgroundColumnsByRow[row.key]?.includes(colIndex) || false;

                    return (
                      <TableCell
                        key={colIndex}
                        align='right'
                        sx={{
                          fontWeight: row.isTotal ? 700 : 400,
                          backgroundColor: getCellBackgroundColor(
                            'inherit',
                            shouldHaveGrayBackground
                          ),
                          color: isBilateral ? '#008207' : 'inherit',
                          border: '1px solid #ddd',
                          py: 0.5,
                          textAlign: 'right',
                        }}
                      >
                        {columnKey
                          ? getFormattedValueFromData(
                              'section1',
                              row.key,
                              columnKey,
                              row.key === 'adjustmentFactorGrossToNetFromNo3'
                            )
                          : ''}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}

              {/* Section 2: Calculation of the number of items by format */}
              <TableRow sx={{ backgroundColor: '#E7EFF5' }}>
                <TableCell
                  colSpan={12}
                  sx={{
                    fontWeight: 700,
                    fontSize: '14px',
                    padding: '8px',
                    backgroundColor: '#E7EFF5',
                    color: '#063142',
                    textAlign: 'left',
                    border: '1px solid #ddd',
                  }}
                >
                  2. Calculation of the number of items by format
                </TableCell>
              </TableRow>

              {section2Rows.map((row, index) => (
                <TableRow
                  key={`section2-${index}`}
                  sx={{
                    backgroundColor: getCellBackgroundColor('inherit'),
                    '&:nth-of-type(even)': {
                      backgroundColor: getCellBackgroundColor('#fafafa'),
                    },
                  }}
                >
                  <TableCell
                    sx={{
                      fontWeight: row.isSubtitle || row.isTotal ? 700 : 500,
                      borderRight: '2px solid #ddd',
                      backgroundColor: getCellBackgroundColor('inherit'),
                      fontStyle: row.isSubtitle ? 'italic' : 'normal',
                      border: '1px solid #ddd',
                      py: 0.5,
                    }}
                  >
                    {row.label} {row.unit && `(${row.unit})`}
                  </TableCell>
                  {columnHeaders.map((_, colIndex) => {
                    if (row.isSubtitle) {
                      return (
                        <TableCell
                          key={colIndex}
                          align='right'
                          sx={{
                            fontWeight: 700,
                            backgroundColor: getCellBackgroundColor('inherit'),
                            border: '1px solid #ddd',
                            py: 0.5,
                            textAlign: 'right',
                          }}
                        ></TableCell>
                      );
                    }

                    // Get the appropriate column keys for this row
                    const columnKeys = getColumnKeysForRow('section2', row.key);
                    const columnKey = columnKeys[colIndex];
                    const isBilateral = columnKey
                      ? isBilateralSource('section2', row.key, columnKey)
                      : false;

                    // Define columns that should have gray background for Section 2 rows
                    const grayBackgroundColumnsByRowSection2: { [key: string]: number[] } = {
                      netWeightFromFormatSeparatedDespatches: [4, 5], // PGE Mixed (sampled flow), PGE Mixed (non-sampled flow)
                      ipkFromFormatSeparatedNo3: [3, 4, 5], // E Encombrant (non-sampled flow), PGE Mixed (sampled flow), PGE Mixed (non-sampled flow)
                      totalNumberOfItemsFormatSeparatedDespatches: [4, 5], // PGE Mixed (sampled flow), PGE Mixed (non-sampled flow)
                      netWeightFromMixedDespatches: [0, 1, 2, 5, 6], // P Petit, G Grand, E Encombrant (sampled flow), PGE Mixed (non-sampled flow), C Postcards
                      netWeightProportionByFormat: [3, 4, 5, 6], // E Encombrant (non-sampled flow), PGE Mixed (sampled flow), PGE Mixed (non-sampled flow), C Postcards
                      netWeightByFormat: [3, 4, 5, 6], // E Encombrant (non-sampled flow), PGE Mixed (sampled flow), PGE Mixed (non-sampled flow), C Postcards
                      ipkFromMixedNo3: [3, 4, 5, 6], // E Encombrant (non-sampled flow), PGE Mixed (sampled flow), PGE Mixed (non-sampled flow), C Postcards
                      totalNumberOfItemsMixedDespatches: [4, 5, 6], // PGE Mixed (sampled flow), PGE Mixed (non-sampled flow), C Postcards
                    };
                    const shouldHaveGrayBackgroundSection2 =
                      grayBackgroundColumnsByRowSection2[row.key]?.includes(colIndex) || false;

                    return (
                      <TableCell
                        key={colIndex}
                        align='right'
                        sx={{
                          fontWeight: row.isSubtitle || row.isTotal ? 700 : 400,
                          backgroundColor: getCellBackgroundColor(
                            'inherit',
                            shouldHaveGrayBackgroundSection2
                          ),
                          color: isBilateral ? '#008207' : 'inherit',
                          border: '1px solid #ddd',
                          py: 0.5,
                          textAlign: 'right',
                        }}
                      >
                        {columnKey
                          ? getFormattedValueFromData(
                              'section2',
                              row.key,
                              columnKey,
                              row.key === 'netWeightProportionByFormat' ||
                                row.key === 'ipkFromFormatSeparatedNo3' ||
                                row.key === 'ipkFromMixedNo3'
                            )
                          : ''}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}

              {/* Section 3: Totals to CN61 */}
              <TableRow sx={{ backgroundColor: '#E7EFF5' }}>
                <TableCell
                  colSpan={12}
                  sx={{
                    fontWeight: 700,
                    fontSize: '14px',
                    padding: '8px',
                    backgroundColor: '#E7EFF5',
                    color: '#063142',
                    textAlign: 'left',
                    border: '1px solid #ddd',
                  }}
                >
                  3. Totals to CN61
                </TableCell>
              </TableRow>

              {section3Rows.map((row, index) => (
                <TableRow
                  key={`section3-${index}`}
                  sx={{
                    backgroundColor: getCellBackgroundColor('inherit'),
                  }}
                >
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      borderRight: '2px solid #ddd',
                      backgroundColor: getCellBackgroundColor('inherit'),
                      border: '1px solid #ddd',
                      py: 0.5,
                    }}
                  >
                    {row.label} {row.unit && `(${row.unit})`}
                  </TableCell>
                  {columnHeaders.map((_, colIndex) => {
                    // Get the appropriate column keys for this row
                    const columnKeys = getColumnKeysForRow('section3', row.key);
                    const columnKey = columnKeys[colIndex];
                    const isBilateral = columnKey
                      ? isBilateralSource('section3', row.key, columnKey)
                      : false;

                    // Define columns that should have gray background for Section 3 rows
                    const grayBackgroundColumnsByRowSection3: { [key: string]: number[] } = {
                      totalNetWeightToBeAccounted: [4, 5, 8, 9, 10], // PGE Mixed (sampled flow), PGE Mixed (non-sampled flow), Registered R, Insured V, Express
                      totalNumberOfItemsToBeAccounted: [4, 5, 7], // PGE Mixed (sampled flow), PGE Mixed (non-sampled flow), M Bags Subject to TD
                    };
                    const shouldHaveGrayBackgroundSection3 =
                      grayBackgroundColumnsByRowSection3[row.key]?.includes(colIndex) || false;

                    return (
                      <TableCell
                        key={colIndex}
                        align='right'
                        sx={{
                          fontWeight: 700,
                          backgroundColor: getCellBackgroundColor(
                            'inherit',
                            shouldHaveGrayBackgroundSection3
                          ),
                          color: isBilateral ? '#008207' : 'inherit',
                          border: '1px solid #ddd',
                          py: 0.5,
                          textAlign: 'right',
                        }}
                      >
                        {columnKey ? getFormattedValueFromData('section3', row.key, columnKey) : ''}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default NO4ReportTable;
